import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Copy, Check, Phone, Clock, Target, Zap, AlertTriangle, ChevronDown, ChevronUp, MessageSquare, Calendar, User, FileText, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

// Theme
const theme = {
  bg: '#f5f3f0',
  card: '#ffffff',
  border: '#e0dbd4',
  text: '#1a1a1a',
  textMuted: 'rgba(26,26,26,0.6)',
  orange: '#F05A1A',
  green: '#16a34a',
  yellow: '#d97706',
  red: '#dc2626',
};

// Access code for Setter pages
const SETTER_CODE = 'setter5';

// Lock Screen Component
const LockScreen = ({ onUnlock }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.toLowerCase() === SETTER_CODE) {
      onUnlock();
    } else {
      setError(true);
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: theme.bg }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 max-w-md w-full text-center"
      >
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(22,163,74,0.1)' }}>
          <Lock className="w-8 h-8" style={{ color: theme.green }} />
        </div>
        <h1 className="font-bebas text-3xl text-gray-800 mb-2">SCRIPT APPEL SETTER</h1>
        <p className="text-gray-500 mb-6">Entrez le code d'accès Setter pour continuer</p>
        
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Code d'accès"
            className={`w-full px-4 py-3 rounded-xl border-2 text-center font-bold tracking-widest uppercase outline-none transition-colors ${
              error ? 'border-red-500 bg-red-50' : 'border-gray-200 focus:border-green-500'
            }`}
            data-testid="setter-call-code-input"
          />
          {error && <p className="text-red-500 text-sm mt-2">Code incorrect</p>}
          <button
            type="submit"
            className="w-full mt-4 py-3 rounded-xl font-bold text-white transition-all hover:scale-105"
            style={{ backgroundColor: theme.green }}
            data-testid="setter-call-unlock-btn"
          >
            DÉVERROUILLER
          </button>
        </form>
        
        <Link 
          to="/recrutement?tab=setter" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-600 text-sm mt-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au recrutement
        </Link>
      </motion.div>
    </div>
  );
};

// Copy button component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
      style={{ 
        backgroundColor: copied ? 'rgba(22,163,74,0.1)' : 'rgba(240,90,26,0.1)',
        color: copied ? theme.green : theme.orange,
        border: `1px solid ${copied ? 'rgba(22,163,74,0.3)' : 'rgba(240,90,26,0.3)'}`
      }}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copié !' : 'Copier'}
    </motion.button>
  );
};

// Script block component
const ScriptBlock = ({ phase, duration, title, content, note, copyText }) => (
  <motion.div
    initial={{ opacity: 0, x: -10 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm"
  >
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span 
          className="text-[10px] font-bold uppercase px-2 py-1 rounded-full"
          style={{ backgroundColor: 'rgba(240,90,26,0.1)', color: theme.orange }}
        >
          {phase}
        </span>
        {duration && (
          <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-600">
            {duration}
          </span>
        )}
      </div>
      {copyText && <CopyButton text={copyText} />}
    </div>
    <h3 className="font-bold text-gray-800 mb-3">{title}</h3>
    <div 
      className="text-sm leading-relaxed p-4 rounded-lg whitespace-pre-line"
      style={{ backgroundColor: '#fafaf8', color: theme.text }}
    >
      {content}
    </div>
    {note && (
      <p className="text-xs mt-3 italic" style={{ color: theme.textMuted }}>
        → {note}
      </p>
    )}
  </motion.div>
);

// Objection card component
const ObjectionCard = ({ objection, response, isOpen, onClick }) => (
  <motion.div
    layout
    onClick={onClick}
    className="rounded-xl overflow-hidden cursor-pointer transition-all bg-white border"
    style={{ borderColor: isOpen ? theme.orange : '#e0dbd4' }}
  >
    <div className="p-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <span className="text-red-500">🚫</span>
        <span className="text-sm font-bold text-gray-700">{objection}</span>
      </div>
      <motion.div animate={{ rotate: isOpen ? 180 : 0 }}>
        <ChevronDown className="w-5 h-5" style={{ color: theme.orange }} />
      </motion.div>
    </div>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
        >
          <div className="px-4 pb-4 border-t border-gray-100 pt-3">
            <div className="flex justify-end mb-2">
              <CopyButton text={response} />
            </div>
            <div 
              className="text-sm leading-relaxed p-4 rounded-lg whitespace-pre-line"
              style={{ backgroundColor: '#fafaf8', color: theme.text }}
            >
              {response}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

// Expandable section
const Section = ({ title, icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{icon}</span>
          <span className="font-bold text-gray-800">{title}</span>
        </div>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }}>
          <ChevronDown className="w-5 h-5" style={{ color: theme.orange }} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Rule card
const RuleCard = ({ icon, title, description }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
    <span className="text-lg">{icon}</span>
    <div>
      <p className="font-bold text-gray-800 text-sm">{title}</p>
      <p className="text-xs text-gray-600 mt-0.5">{description}</p>
    </div>
  </div>
);

// Preparation item
const PrepItem = ({ icon, title, content }) => (
  <div className="flex items-start gap-3 p-4 rounded-lg bg-gray-50 border border-gray-200">
    <span className="text-lg">{icon}</span>
    <div>
      <p className="font-bold text-gray-800 text-sm mb-1">{title}</p>
      <p className="text-sm text-gray-600 leading-relaxed">{content}</p>
    </div>
  </div>
);

const SetterCallScriptPage = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [openObjection, setOpenObjection] = useState(null);

  const objections = [
    {
      objection: "J'ai pas le temps là.",
      response: `Pas de souci du tout. Je te prends 30 secondes — t'as rempli le diagnostic ce matin, je voulais juste savoir quel point t'avait le plus parlé.

Je te rappelle quand tu veux, tu me dis toi-même.`
    },
    {
      objection: "C'est quoi exactement votre accompagnement ?",
      response: `C'est justement pour ça que Sabril fait cet échange de 30 minutes — pour t'expliquer concrètement ce que ça change pour un restaurant comme le tien.

Mon rôle à moi c'est juste de voir si le profil correspond. Et là ce que tu me dis sur [leur problème], ça ressemble exactement aux cas qu'il traite.

Je t'envoie le lien — t'as qu'à choisir le créneau.`
    },
    {
      objection: "C'est combien ?",
      response: `Le call avec Sabril est gratuit — c'est lui qui t'explique tout ça directement, budget compris.

Mon rôle c'est juste de voir si ça vaut le coup de te mettre en relation. Et au vu de ce que tu me dis, oui clairement.

Je t'envoie le lien.`
    },
    {
      objection: "J'ai déjà essayé des formations, ça n'a rien changé.",
      response: `Je comprends. Et c'est exactement pour ça que ce que fait Sabril c'est pas une formation.

C'est un accompagnement sur 90 jours — il travaille directement sur tes chiffres, ton organisation, ta réalité à toi.

Mais c'est lui qui t'explique la différence — 30 minutes et tu vois si c'est pertinent. Rien à perdre.`
    },
    {
      objection: "Je vais réfléchir.",
      response: `Bien sûr. Tu réfléchis à quoi exactement ? Le créneau, le principe de l'échange ?

[ Il répond ]

Ok. Écoute, le call est gratuit — le seul risque c'est de passer 30 minutes avec Sabril et de repartir avec des idées concrètes.

Je te laisse le lien, tu peux même booker pour dans une semaine si t'as besoin de souffler.`
    },
    {
      objection: "C'est quoi Sabril exactement ?",
      response: `Sabril c'est le fondateur de Maison Avocado — il a construit son restaurant pour qu'il tourne sans lui, avec de fortes marges.

Depuis il accompagne des restaurateurs qui veulent faire pareil — reprendre le contrôle de leurs chiffres et de leur temps.

Il t'explique tout ça mieux que moi sur le call.`
    },
    {
      objection: "Je suis pas sûr que ça marche pour mon type de resto.",
      response: `C'est une vraie question. C'est exactement pour ça que le call existe — Sabril regarde ta situation concrète et te dit franchement si il peut t'aider ou pas.

Il refuse des profils qui ne correspondent pas. Donc si il prend le call avec toi, c'est qu'il voit quelque chose.`
    }
  ];

  if (!isUnlocked) {
    return <LockScreen onUnlock={() => setIsUnlocked(true)} />;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.bg }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-6">
        <div className="max-w-3xl mx-auto">
          <Link 
            to="/recrutement?tab=setter" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la page Setter
          </Link>
          <h1 className="text-white font-bebas text-4xl tracking-wide flex items-center gap-3">
            <Phone className="w-8 h-8" />
            SCRIPT APPEL SETTER
          </h1>
          <p className="text-white/80 text-sm mt-2">
            Appel sortant · Objectif : poser un RDV visio · Version 1.0
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        
        {/* Objectif */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-green-50 to-white rounded-xl p-6 border-2 border-green-300"
        >
          <div className="flex items-center gap-3 mb-3">
            <Target className="w-6 h-6 text-green-600" />
            <h2 className="font-bebas text-2xl text-green-600">OBJECTIF DE L'APPEL</h2>
          </div>
          <p className="text-gray-700 leading-relaxed font-semibold">
            Un seul objectif : poser un RDV visio sur Calendly avec Sabril ou le closer.
          </p>
          <p className="text-gray-600 mt-2">
            Pas vendre le programme. Pas expliquer la méthode. Juste convaincre que ça vaut 30 minutes de leur temps.
          </p>
        </motion.div>

        {/* Préparation */}
        <Section title="PRÉPARATION AVANT L'APPEL" icon="📋" defaultOpen>
          <div className="space-y-4">
            {/* Délai d'appel */}
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-yellow-600" />
                <h4 className="font-bold text-yellow-700">Délai d'appel</h4>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                <strong>Appeler dans les 2h</strong> qui suivent le diagnostic rempli. C'est le moment où le lead est le plus chaud — il vient de se confronter à ses problèmes.
              </p>
              <p className="text-gray-600 text-sm mt-2">
                Au-delà de 24h : le lead refroidit fortement. Passé 48h : taux de conversion divisé par 2.
              </p>
            </div>

            {/* Ce qu'il faut avoir */}
            <PrepItem 
              icon="👁️" 
              title="Ce qu'il faut avoir sous les yeux" 
              content="• Le score du diagnostic du lead (si accessible via audit-resto.fr)
• Son prénom
• Son type de restaurant (si visible sur son profil Instagram)
• Le lien Calendly prêt à être envoyé par SMS ou DM dès la fin de l'appel"
            />

            {/* État d'esprit */}
            <PrepItem 
              icon="🧠" 
              title="État d'esprit" 
              content="• Tu n'es pas un commercial. Tu es quelqu'un de l'équipe de Sabril qui fait un suivi personnalisé.
• Tu appelles parce que le diagnostic a été rempli — c'est lui qui a levé la main.
• Tu ne supplies pas. Tu proposes. S'il n'est pas intéressé, tu passes."
            />
          </div>
        </Section>

        {/* Script d'appel */}
        <Section title="SCRIPT D'APPEL" icon="📞" defaultOpen>
          <div className="space-y-4">
            <ScriptBlock
              phase="PHASE 1"
              duration="15 SEC"
              title="ACCROCHE"
              content={`Bonjour [Prénom], c'est [Ton prénom], je fais partie de l'équipe de Sabril — Audit-Resto.

Je t'appelle parce que t'as rempli le diagnostic tout à l'heure, et je voulais juste faire un retour rapide avec toi.

T'as 2-3 minutes ?`}
              note="S'il dit oui : tu continues. S'il dit non : « Pas de souci, je te rappelle quand ? »"
              copyText={`Bonjour [Prénom], c'est [Ton prénom], je fais partie de l'équipe de Sabril — Audit-Resto.

Je t'appelle parce que t'as rempli le diagnostic tout à l'heure, et je voulais juste faire un retour rapide avec toi.

T'as 2-3 minutes ?`}
            />

            <ScriptBlock
              phase="PHASE 2"
              duration="30 SEC"
              title="ANCRAGE DANS LE DIAGNOSTIC"
              content={`Super. Donc t'as fait le diagnostic — tu as vu ton score ?

[ Il répond ]

Ok. Et le point qui t'a le plus parlé dans les résultats, c'était quoi ?`}
              note="Le laisser parler. Ne pas l'interrompre. C'est lui qui pose les bases du problème."
              copyText={`Super. Donc t'as fait le diagnostic — tu as vu ton score ?

Ok. Et le point qui t'a le plus parlé dans les résultats, c'était quoi ?`}
            />

            <ScriptBlock
              phase="PHASE 3"
              duration="45 SEC"
              title="QUALIFIER RAPIDEMENT"
              content={`Je vois. Et là dans ton restaurant, c'est quoi le truc qui te pèse le plus en ce moment ?

[ Il parle ]

Et ça dure depuis combien de temps à peu près ?`}
              note="Objectif : faire parler. Plus il parle de ses problèmes, plus il se convainc lui-même."
              copyText={`Je vois. Et là dans ton restaurant, c'est quoi le truc qui te pèse le plus en ce moment ?

Et ça dure depuis combien de temps à peu près ?`}
            />

            <ScriptBlock
              phase="PHASE 4"
              duration="20 SEC"
              title="CRÉER L'URGENCE"
              content={`Et si ça continue exactement comme ça dans 6 mois — ça te va ou ça doit changer ?`}
              note="S'il dit « ça doit changer » : tu passes à la transition. S'il dit « ça va » : creuse encore."
              copyText={`Et si ça continue exactement comme ça dans 6 mois — ça te va ou ça doit changer ?`}
            />

            <ScriptBlock
              phase="PHASE 5"
              duration="30 SEC"
              title="TRANSITION VERS LE CALL"
              content={`Ok je vois exactement ta situation.

Ce que fait Sabril avec les restaurateurs qu'il accompagne, c'est précisément ça — identifier où ça fuit et mettre en place les bons systèmes.

Il a un échange de 30 minutes cette semaine pour voir si ton profil correspond à ce qu'il accompagne.

C'est gratuit, sans engagement — juste pour voir si ça fait sens pour toi.

Je t'envoie le lien tout de suite, t'as qu'à choisir le créneau qui t'arrange.`}
              note="Envoyer le lien Calendly immédiatement par SMS ou DM Instagram pendant qu'il est encore en ligne."
              copyText={`Ok je vois exactement ta situation.

Ce que fait Sabril avec les restaurateurs qu'il accompagne, c'est précisément ça — identifier où ça fuit et mettre en place les bons systèmes.

Il a un échange de 30 minutes cette semaine pour voir si ton profil correspond à ce qu'il accompagne.

C'est gratuit, sans engagement — juste pour voir si ça fait sens pour toi.

Je t'envoie le lien tout de suite, t'as qu'à choisir le créneau qui t'arrange.`}
            />

            <ScriptBlock
              phase="PHASE 6"
              duration="15 SEC"
              title="CONFIRMER"
              content={`Tu l'as reçu ?

[ Il confirme ]

Parfait. Sabril te parle directement sur le call. À [date/heure choisie] alors.`}
              copyText={`Tu l'as reçu ?

Parfait. Sabril te parle directement sur le call. À [date/heure choisie] alors.`}
            />
          </div>
        </Section>

        {/* Objections */}
        <Section title="OBJECTIONS & RÉPONSES" icon="🚫">
          <div className="space-y-3">
            {objections.map((obj, i) => (
              <ObjectionCard
                key={i}
                objection={obj.objection}
                response={obj.response}
                isOpen={openObjection === i}
                onClick={() => setOpenObjection(openObjection === i ? null : i)}
              />
            ))}
          </div>
        </Section>

        {/* Situations particulières */}
        <Section title="SITUATIONS PARTICULIÈRES" icon="⚠️">
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-bold text-gray-800 text-sm mb-2">Il ne répond pas</p>
              <p className="text-gray-600 text-sm mb-3">
                Rappeler 2 fois maximum : une fois dans les 2h, une fois le lendemain matin. Après, envoyer un DM :
              </p>
              <div className="bg-white rounded p-3 text-sm text-gray-700 border border-gray-200">
                [Prénom] j'ai essayé de t'appeler — t'as eu le temps de regarder ton score de diagnostic ?
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-bold text-gray-800 text-sm mb-2">Il décroche mais repose tout de suite</p>
              <div className="bg-white rounded p-3 text-sm text-gray-700 border border-gray-200 mb-2">
                Pas de souci — je te send le lien directement, tu regardes quand t'as 2 minutes.
                À bientôt
              </div>
              <p className="text-xs text-gray-500">
                → Envoyer le lien Calendly en DM immédiatement avec le message : « Voilà le lien de Sabril → [lien] »
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-bold text-gray-800 text-sm mb-2">Il a oublié qu'il avait rempli le diagnostic</p>
              <div className="bg-white rounded p-3 text-sm text-gray-700 border border-gray-200">
                Pas de souci — t'as rempli un petit diagnostic sur audit-resto.fr tout à l'heure, 8 questions sur ton restaurant.

                Je t'appelle juste pour faire un retour rapide sur ton score. T'as 2 minutes ?
              </div>
            </div>
          </div>
        </Section>

        {/* Règles d'or */}
        <Section title="RÈGLES D'OR" icon="⚡" defaultOpen>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <RuleCard 
              icon="⏱️" 
              title="Appeler dans les 2h" 
              description="C'est là que le lead est le plus chaud." 
            />
            <RuleCard 
              icon="🎯" 
              title="Un seul objectif" 
              description="Poser le RDV. Pas expliquer le programme." 
            />
            <RuleCard 
              icon="👂" 
              title="Laisser parler" 
              description="Plus il parle de ses problèmes, plus il se convainc." 
            />
            <RuleCard 
              icon="📲" 
              title="Envoyer le lien pendant l'appel" 
              description="Pas après. Pas dans 10 min. Pendant." 
            />
            <RuleCard 
              icon="🚫" 
              title="Ne jamais pitcher le prix" 
              description="C'est le rôle de Sabril sur le call." 
            />
            <RuleCard 
              icon="😌" 
              title="Rester calme sur les objections" 
              description="Ne pas défendre. Reformuler et proposer le call." 
            />
            <RuleCard 
              icon="📊" 
              title="Utiliser le diagnostic comme ancre" 
              description="C'est lui qui a levé la main — rappelle-le-lui." 
            />
            <RuleCard 
              icon="👍" 
              title="Si vraiment pas intéressé" 
              description="« Pas de souci, bonne continuation » et on passe." 
            />
          </div>
          
          <div className="mt-6 p-4 rounded-xl bg-green-50 border-2 border-green-300">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-green-600" />
              <span className="font-bold text-green-700">Objectif final</span>
            </div>
            <p className="text-green-700">
              <strong>Obtenir un RDV booké sur Calendly.</strong> Rien d'autre.
            </p>
          </div>
        </Section>

        {/* Footer */}
        <div className="text-center pt-8 pb-12">
          <div className="flex flex-col sm:flex-row justify-center gap-3 mb-6">
            <Link 
              to="/setter-guide"
              className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-xl transition-colors"
            >
              <MessageSquare className="w-4 h-4" />
              Guide DM Setter
            </Link>
            <Link 
              to="/recrutement?tab=setter"
              className="inline-flex items-center justify-center gap-2 bg-gray-600 hover:bg-gray-700 text-white font-bold px-6 py-3 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour page Setter
            </Link>
          </div>
          <p className="text-gray-400 text-xs">
            © 2025 PRSNL · Script Appel Setter v1.0
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetterCallScriptPage;
